import ChevronLeftIcon from '@material-design-icons/svg/filled/chevron_left.svg?react';
import ChevronRightIcon from '@material-design-icons/svg/filled/chevron_right.svg?react';
import HistoryIcon from '@material-design-icons/svg/filled/history.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import type { RightResult, RouterOutputs } from '../../types/TRPC';
import { SectionCard, SectionHeader, StatusPill } from './primitives';
import { Button } from './uiButton';
import {
  formatDateTime,
  formatDuration,
  formatRelative,
  formatRunStatus,
  getRunStatusVariant,
} from './runFormatting';

type RunsListResult = RightResult<RouterOutputs['listDappReviewSummaryRuns']>;
type RunListItem = RunsListResult['runs'][number];

interface Props {
  runs: RunListItem[];
  loading: boolean;
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  onSelect: (runId: string) => void;
  onPageChange: (next: number) => void;
  onRefresh: () => void;
}

export function DappReviewSummaryRunsTable({
  runs,
  loading,
  total,
  page,
  pageSize,
  totalPages,
  onSelect,
  onPageChange,
  onRefresh,
}: Props) {
  const showingFrom = total === 0 ? 0 : page * pageSize + 1;
  const showingTo = Math.min(total, (page + 1) * pageSize);

  return (
    <SectionCard>
      <SectionHeader
        eyebrow="History"
        title="Recent runs"
        description="Every recorded Claude review summary run. Click a row to inspect its per-app summaries and inputs."
        actions={
          <Button
            variant="secondary"
            size="md"
            onClick={onRefresh}
            disabled={loading}
            submitting={loading}
          >
            {!loading && <RefreshIcon className="size-3.5 fill-current" />}
            Refresh
          </Button>
        }
      />

      {runs.length === 0 && !loading ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-white/[0.06] text-[11px] uppercase tracking-[0.12em] text-neutral-500">
                <th className="px-6 py-3 text-left font-medium">Started</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Trigger</th>
                <th className="px-4 py-3 text-right font-medium">Apps</th>
                <th className="px-4 py-3 text-right font-medium">Summaries</th>
                <th className="px-4 py-3 text-right font-medium">
                  Reviews used
                </th>
                <th className="px-4 py-3 text-right font-medium">Errors</th>
                <th className="px-4 py-3 text-right font-medium">Emails</th>
                <th className="px-4 py-3 text-right font-medium">Duration</th>
                <th className="w-10 px-2 py-3" aria-hidden />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {runs.map((run) => {
                const duration = formatDuration(run.startedAt, run.completedAt);
                const variant = getRunStatusVariant(run.status);
                const relative = formatRelative(run.startedAt);
                const summaryEmailsSent = run.summaryEmailsSent ?? 0;
                const summaryEmailsSkipped = run.summaryEmailsSkipped ?? 0;
                const summaryEmailFailures = run.summaryEmailFailures ?? 0;

                return (
                  <tr
                    key={run.id}
                    tabIndex={0}
                    role="button"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        onSelect(run.id);
                      }
                    }}
                    onClick={() => onSelect(run.id)}
                    className="group cursor-pointer outline-none transition-colors hover:bg-white/[0.02] focus-visible:bg-white/[0.04]"
                  >
                    <td className="px-6 py-3.5">
                      <div className="text-white">
                        {formatDateTime(run.startedAt)}
                      </div>
                      {relative && (
                        <div className="mt-0.5 text-[12px] text-neutral-500">
                          {relative}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusPill variant={variant}>
                        {formatRunStatus(run.status)}
                      </StatusPill>
                    </td>
                    <td className="px-4 py-3.5 text-neutral-300">
                      {run.trigger}
                    </td>
                    <td className="px-4 py-3.5 text-right text-white tabular-nums">
                      {run.appsScanned}
                    </td>
                    <td className="px-4 py-3.5 text-right text-white tabular-nums">
                      {run.summariesWritten}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      <span className="text-white">{run.reviewsIncluded}</span>
                      <span className="text-neutral-500">
                        /{run.reviewsFetched}
                      </span>
                    </td>
                    <td
                      className={`px-4 py-3.5 text-right tabular-nums ${
                        run.appErrors > 0 ? 'text-rose-300' : 'text-neutral-500'
                      }`}
                    >
                      {run.appErrors}
                    </td>
                    <td className="px-4 py-3.5 text-right tabular-nums">
                      <span className="text-white">{summaryEmailsSent}</span>
                      <span className="text-neutral-500">
                        /{summaryEmailsSkipped + summaryEmailFailures}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right text-neutral-300 tabular-nums">
                      {duration ?? '—'}
                    </td>
                    <td className="px-2 py-3.5 text-right">
                      <ChevronRightIcon className="size-4 fill-neutral-600 transition-colors group-hover:fill-white" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="flex flex-col gap-3 border-t border-white/[0.06] px-6 py-3 md:flex-row md:items-center md:justify-between">
        <div className="text-[12px] text-neutral-500">
          {total === 0
            ? 'No runs to show'
            : `Showing ${showingFrom}–${showingTo} of ${total}`}
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.max(0, page - 1))}
            disabled={loading || page === 0}
          >
            <ChevronLeftIcon className="size-3.5 fill-current" />
            Previous
          </Button>
          <div className="px-2 text-[12px] tabular-nums text-neutral-500">
            Page {page + 1} of {totalPages}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(Math.min(totalPages - 1, page + 1))}
            disabled={loading || page + 1 >= totalPages}
          >
            Next
            <ChevronRightIcon className="size-3.5 fill-current" />
          </Button>
        </div>
      </div>
    </SectionCard>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      <div className="flex size-12 items-center justify-center rounded-full border border-white/[0.06] bg-white/[0.02]">
        <HistoryIcon className="size-5 fill-neutral-500" />
      </div>
      <div>
        <div className="text-[14px] font-medium text-white">No runs yet</div>
        <p className="mt-1 text-[12px] text-neutral-500">
          Trigger one from the configuration panel above.
        </p>
      </div>
    </div>
  );
}
